import './App.css';
import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Clock, IconContext, Key, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import AuthModal from './components/auth/AuthModal';
import TelegramBind from './components/auth/TelegramBind';
import Layout from './components/layout/Layout';
import Home from './pages/Home';
import api, { apiError } from './lib/api';
import { revealTenantBoot } from './lib/brand';

const Tasks = lazy(() => import('./pages/Tasks'));
const Market = lazy(() => import('./pages/Market'));
const Games = lazy(() => import('./pages/Games'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const News = lazy(() => import('./pages/News'));
const Wheel = lazy(() => import('./pages/Wheel'));
const PromoCode = lazy(() => import('./pages/PromoCode'));
const Profile = lazy(() => import('./pages/Profile'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const Notifications = lazy(() => import('./pages/Notifications'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const LicensePanel = lazy(() => import('./pages/LicensePanel'));
const SetupWizard = lazy(() => import('./pages/SetupWizard'));

const PageFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center">
    <Loader2 className="w-7 h-7 animate-spin text-bh-accent" />
  </div>
);

const ModuleGate = ({ mk, children }) => {
  const { modules } = useSettings();
  if (modules[mk] === false) {
    return (
      <div data-testid="module-disabled" className="flex flex-col items-center justify-center py-28 text-center">
        <div className="w-16 h-16 rounded-2xl bg-bh-accent/10 border border-bh-accent/30 flex items-center justify-center mb-5">
          <Clock className="w-7 h-7 text-bh-accent" />
        </div>
        <p className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Yakında<span className="text-accent">!</span></p>
        <p className="text-sm text-gray-500 mt-3">Bu bölüm çok yakında açılıyor. Takipte kal!</p>
      </div>
    );
  }
  return children;
};

const LicenseLocked = ({ onMoved }) => {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/license/move', { key: key.trim() });
      onMoved(data.setup_needed ? 'setup' : 'ok');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-bh-canvas flex items-center justify-center p-4" data-testid="license-locked">
      <form onSubmit={submit} className="panel top-light p-8 max-w-sm w-full text-center space-y-4">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-bh-crimson/10 border border-bh-crimson/30 flex items-center justify-center">
          <Key className="w-6 h-6 text-bh-crimson" />
        </div>
        <div>
          <h1 className="font-display font-extrabold text-xl">Domain bağlı değil</h1>
          <p className="text-sm text-gray-500 mt-2">Yeni domaine geçiyorsan lisans anahtarını yaz. Üyeler, puanlar, sponsorlar silinmez — site bu adreste açılır.</p>
        </div>
        <input
          className="w-full h-10 px-3 rounded-lg bg-bh-canvas border border-white/10 text-sm text-left placeholder:text-gray-600 focus:outline-none focus:border-bh-accent/60"
          placeholder="DBEY-… lisans anahtarı"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          required
          data-testid="license-move-key"
        />
        {error ? <p className="text-xs text-bh-crimson bg-bh-crimson/10 border border-bh-crimson/30 rounded-lg px-3 py-2">{error}</p> : null}
        <button type="submit" disabled={busy} className="w-full btn-accent py-3 rounded-xl text-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Bu domaine taşı'}
        </button>
      </form>
    </div>
  );
};

function LicensedApp() {
  const [gate, setGate] = useState('loading');
  useEffect(() => {
    api.get('/license')
      .then((r) => setGate(r.data.setup_needed ? 'setup' : 'ok'))
      .catch((e) => setGate(e?.response?.status === 403 ? 'locked' : 'ok'));
  }, []);
  useEffect(() => {
    if (gate === 'locked' || gate === 'setup') revealTenantBoot();
  }, [gate]);
  if (gate === 'loading') {
    return <div className="min-h-screen bg-bh-canvas" />;
  }
  if (gate === 'locked') return <LicenseLocked onMoved={setGate} />;
  if (gate === 'setup') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-bh-canvas flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-bh-accent" /></div>}>
        <SetupWizard onDone={() => setGate('ok')} />
      </Suspense>
    );
  }
  return (
    <AuthProvider>
      <SettingsProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/panel-bh2025" element={<AdminPanel />} />
            <Route
              path="/*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/sponsorlar" element={<Home />} />
                    <Route path="/gorevler" element={<ModuleGate mk="tasks"><Tasks /></ModuleGate>} />
                    <Route path="/market" element={<ModuleGate mk="market"><Market /></ModuleGate>} />
                    <Route path="/oyunlar" element={<ModuleGate mk="games"><Games /></ModuleGate>} />
                    <Route path="/oyunlar/:gameKey" element={<ModuleGate mk="games"><Games /></ModuleGate>} />
                    <Route path="/turnuvalar" element={<ModuleGate mk="tournaments"><Tournaments /></ModuleGate>} />
                    <Route path="/liderlik" element={<ModuleGate mk="leaderboard"><Leaderboard /></ModuleGate>} />
                    <Route path="/haberler" element={<ModuleGate mk="news"><News /></ModuleGate>} />
                    <Route path="/cark" element={<ModuleGate mk="wheel"><Wheel /></ModuleGate>} />
                    <Route path="/promokod" element={<ModuleGate mk="promo"><PromoCode /></ModuleGate>} />
                    <Route path="/profil" element={<Profile />} />
                    <Route path="/sifre-sifirla" element={<PasswordReset />} />
                    <Route path="/bildirimler" element={<Notifications />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </Suspense>
        <AuthModal />
        <TelegramBind />
      </SettingsProvider>
    </AuthProvider>
  );
}

function App() {
  useEffect(() => {
    if (window.location.pathname === '/panel-lisans') revealTenantBoot();
  }, []);
  return (
    <div className="App">
      <IconContext.Provider value={{ weight: 'duotone' }}>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen bg-bh-canvas" />}>
          <Routes>
            <Route path="/panel-lisans" element={<LicensePanel />} />
            <Route path="*" element={<LicensedApp />} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" richColors offset="72px" mobileOffset="68px" />
      </BrowserRouter>
      </IconContext.Provider>
    </div>
  );
}

export default App;
